import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  schedulesApi,
  sellersApi,
  type Schedule,
  type ScheduleInput,
  type SchedulePreview,
  type SellerListItem,
} from '../lib/api';
import { useAuth } from '../lib/auth';
import { WEEKDAYS_RU, fmtMskHuman } from '../lib/weekdays';

// Local form state — kept in plain primitives so the controls stay simple.
type Form = {
  name: string;
  ship_days: Set<number>;
  ship_time_msk: string;
  deadline_anchor: 'absolute' | 'relative';
  deadline_days: Set<number>;
  deadline_time_msk: string;
  deadline_offset_h: number;
  is_default: boolean;
  notes: string;
};

function emptyForm(): Form {
  return {
    name: '',
    ship_days: new Set(),
    ship_time_msk: '10:00',
    deadline_anchor: 'absolute',
    deadline_days: new Set(),
    deadline_time_msk: '23:59',
    deadline_offset_h: 24,
    is_default: false,
    notes: '',
  };
}

function fromSchedule(s: Schedule): Form {
  return {
    name: s.name,
    ship_days: new Set(s.ship_days),
    ship_time_msk: s.ship_time_msk,
    deadline_anchor: s.deadline_anchor,
    deadline_days: new Set(s.deadline_days),
    deadline_time_msk: s.deadline_time_msk,
    deadline_offset_h: s.deadline_offset_h,
    is_default: s.is_default,
    notes: s.notes ?? '',
  };
}

function toInput(f: Form): Omit<ScheduleInput, 'seller_id'> {
  return {
    name: f.name.trim(),
    ship_days: [...f.ship_days].sort((a, b) => a - b),
    ship_time_msk: f.ship_time_msk,
    deadline_anchor: f.deadline_anchor,
    deadline_days: [...f.deadline_days].sort((a, b) => a - b),
    deadline_time_msk: f.deadline_time_msk,
    deadline_offset_h: f.deadline_offset_h,
    is_default: f.is_default,
    notes: f.notes.trim() || null,
  };
}

export default function ScheduleEdit() {
  const { id: idStr } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isNew = !idStr || idStr === 'new';
  const id = isNew ? null : Number(idStr);

  const [form, setForm] = useState<Form>(emptyForm());
  const [sellerId, setSellerId] = useState<number | null>(null);
  const [sellers, setSellers] = useState<SellerListItem[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [preview, setPreview] = useState<SchedulePreview | null>(null);

  // Load sellers list (admin) for the seller picker on create.
  useEffect(() => {
    if (user?.role === 'admin') {
      sellersApi.list().then((r) => {
        setSellers(r.sellers);
        if (isNew && !sellerId && r.sellers.length === 1) setSellerId(r.sellers[0].id);
      }).catch(() => { /* noop */ });
    } else if (user) {
      setSellerId(user.id);
    }
  }, [user, isNew]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Load existing schedule for edit.
  useEffect(() => {
    if (isNew || !id) return;
    schedulesApi.get(id)
      .then((r) => { setForm(fromSchedule(r.schedule)); setSellerId(r.schedule.seller_id); })
      .catch((e) => setErr(String(e?.message ?? e)))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  // Load preview whenever we have an existing id.
  useEffect(() => {
    if (isNew || !id) return;
    schedulesApi.preview(id, 5)
      .then(setPreview)
      .catch(() => { /* preview is best-effort */ });
  }, [id, isNew, form.ship_days, form.ship_time_msk, form.deadline_anchor,
      form.deadline_days, form.deadline_time_msk, form.deadline_offset_h]);

  function toggleSet(name: 'ship_days' | 'deadline_days', day: number) {
    setForm((f) => {
      const next = new Set(f[name]);
      if (next.has(day)) next.delete(day); else next.add(day);
      return { ...f, [name]: next };
    });
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    setSavedMsg(null);
    try {
      if (isNew) {
        if (!sellerId) throw new Error('Не выбран продавец');
        const body: ScheduleInput & { seller_id: number } = { ...toInput(form), seller_id: sellerId };
        const r = await schedulesApi.create(body);
        nav(`/schedules/${r.schedule.id}`, { replace: true });
      } else if (id) {
        await schedulesApi.update(id, toInput(form));
        setSavedMsg('Сохранено');
      }
    } catch (e) {
      setErr(String((e as Error)?.message ?? e));
    } finally {
      setBusy(false);
    }
  }

  async function onDelete() {
    if (!id) return;
    if (!confirm('Удалить расписание? Товары, которые на него ссылаются, останутся без расписания.')) return;
    await schedulesApi.remove(id);
    nav('/schedules', { replace: true });
  }

  if (loading) return <div className="container"><p className="muted">Загрузка…</p></div>;

  return (
    <div className="container">
      <div className="page-head">
        <h2>{isNew ? 'Новое расписание' : `Расписание #${id}`}</h2>
        <div className="row">
          <Link to="/schedules" className="btn">← К списку</Link>
          {!isNew && <button className="btn" onClick={onDelete} type="button">Удалить</button>}
        </div>
      </div>

      <form className="card form" onSubmit={onSubmit}>
        {isNew && user?.role === 'admin' && (
          <label>
            <span>Продавец *</span>
            <select
              value={sellerId ?? ''}
              onChange={(e) => setSellerId(Number(e.target.value))}
              required
            >
              <option value="">— выберите —</option>
              {sellers.map((s) => (
                <option key={s.id} value={s.id}>{s.display_name || s.login}</option>
              ))}
            </select>
          </label>
        )}

        <label>
          <span>Название *</span>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder='Например: "Розы — Пн/Чт"'
            required
          />
        </label>

        <fieldset className="fieldset">
          <legend>Отгрузка</legend>
          <div className="weekday-row">
            {WEEKDAYS_RU.map((w) => (
              <label key={w.iso} className={`chip ${form.ship_days.has(w.iso) ? 'on' : ''}`}>
                <input
                  type="checkbox"
                  checked={form.ship_days.has(w.iso)}
                  onChange={() => toggleSet('ship_days', w.iso)}
                />
                <span>{w.short}</span>
              </label>
            ))}
          </div>
          <label>
            <span>Время отгрузки (МСК)</span>
            <input
              type="time"
              value={form.ship_time_msk}
              onChange={(e) => setForm({ ...form, ship_time_msk: e.target.value })}
              required
            />
          </label>
        </fieldset>

        <fieldset className="fieldset">
          <legend>Дедлайн приёма заказов</legend>
          <label>
            <span>Как считать</span>
            <select
              value={form.deadline_anchor}
              onChange={(e) => setForm({ ...form, deadline_anchor: e.target.value as 'absolute' | 'relative' })}
            >
              <option value="absolute">По дню недели (например, до Вс 23:59 перед отгрузкой)</option>
              <option value="relative">За N часов до отгрузки</option>
            </select>
          </label>

          {form.deadline_anchor === 'absolute' ? (
            <>
              <div className="weekday-row">
                {WEEKDAYS_RU.map((w) => (
                  <label key={w.iso} className={`chip ${form.deadline_days.has(w.iso) ? 'on' : ''}`}>
                    <input
                      type="checkbox"
                      checked={form.deadline_days.has(w.iso)}
                      onChange={() => toggleSet('deadline_days', w.iso)}
                    />
                    <span>{w.short}</span>
                  </label>
                ))}
              </div>
              <label>
                <span>Время дедлайна (МСК)</span>
                <input
                  type="time"
                  value={form.deadline_time_msk}
                  onChange={(e) => setForm({ ...form, deadline_time_msk: e.target.value })}
                />
              </label>
            </>
          ) : (
            <label>
              <span>За сколько часов до отгрузки</span>
              <input
                type="number"
                min={1}
                max={336}
                value={form.deadline_offset_h}
                onChange={(e) => setForm({ ...form, deadline_offset_h: Number(e.target.value) })}
              />
            </label>
          )}
        </fieldset>

        <label className="row">
          <input
            type="checkbox"
            checked={form.is_default}
            onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
          />
          <span>Использовать по умолчанию для этого продавца</span>
        </label>

        <label>
          <span>Заметки</span>
          <textarea
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </label>

        {err && <div className="error">{err}</div>}

        <div className="row">
          <button className="btn primary" disabled={busy} type="submit">
            {busy ? 'Сохраняем…' : (isNew ? 'Создать' : 'Сохранить')}
          </button>
          {savedMsg && <span className="ok-msg">{savedMsg}</span>}
        </div>
      </form>

      {preview && (
        <div className="card sub" style={{ marginTop: 18 }}>
          <h3>Ближайшие окна (от {fmtMskHuman(preview.from)})</h3>
          {preview.windows.length === 0 ? (
            <p className="muted">Не удалось рассчитать — проверь, что выбран хотя бы один день отгрузки.</p>
          ) : (
            <table className="grid compact">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Дедлайн заказов</th>
                  <th>Отгрузка</th>
                </tr>
              </thead>
              <tbody>
                {preview.windows.map((w, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{fmtMskHuman(w.deadline)}</td>
                    <td>{fmtMskHuman(w.ship)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
